import { useEffect } from "react";
import { prefersReducedMotion } from "../../context/PaintContext";

const EASE_MS = 140;

// Pointer depth for the hero film: writes --px/--py (-1..1) on the stage so the film tilts away,
// the copy floats toward the cursor and a soft light follows it. A ring trails the cursor.
// Mouse/trackpad only and stays inside the hero. It's driven only by the visitor's own pointer, so it also runs
// with reduced motion (at half strength).
export const useHeroTilt = (stageRef, ringRef) => {
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return undefined;
    const amp = prefersReducedMotion() ? 0.5 : 1;
    stage.style.setProperty("--tilt-scale", "1.05");
    const cur = { x: 0, y: 0, rx: 0, ry: 0, o: 0 };
    const tgt = { x: 0, y: 0, rx: 0, ry: 0, o: 0 };
    let raf = 0;
    let last = 0;

    const tick = (now) => {
      const k = 1 - Math.exp(-(last ? Math.min(64, now - last) : 16) / EASE_MS);
      last = now;
      let moving = false;
      Object.keys(cur).forEach((key) => {
        cur[key] += (tgt[key] - cur[key]) * k;
        if (Math.abs(tgt[key] - cur[key]) < 0.001) cur[key] = tgt[key];
        else moving = true;
      });
      stage.style.setProperty("--px", cur.x.toFixed(4));
      stage.style.setProperty("--py", cur.y.toFixed(4));
      stage.style.setProperty("--glow", cur.o.toFixed(3));
      const ring = ringRef.current;
      if (ring) {
        ring.style.transform = `translate3d(${cur.rx}px, ${cur.ry}px, 0) translate(-50%, -50%)`;
        ring.style.opacity = String(cur.o);
      }
      raf = moving ? requestAnimationFrame(tick) : 0;
      if (!moving) last = 0;
    };
    const run = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };

    const move = (e) => {
      const r = stage.getBoundingClientRect();
      tgt.x = (((e.clientX - r.left) / r.width) * 2 - 1) * amp;
      tgt.y = (((e.clientY - r.top) / r.height) * 2 - 1) * amp;
      tgt.rx = e.clientX - r.left;
      tgt.ry = e.clientY - r.top;
      if (!cur.o) {
        cur.rx = tgt.rx;
        cur.ry = tgt.ry;
      }
      tgt.o = 1;
      run();
    };
    const leave = () => {
      tgt.x = 0;
      tgt.y = 0;
      tgt.o = 0;
      run();
    };
    stage.addEventListener("pointermove", move);
    stage.addEventListener("pointerleave", leave);
    return () => {
      cancelAnimationFrame(raf);
      stage.removeEventListener("pointermove", move);
      stage.removeEventListener("pointerleave", leave);
    };
  }, [stageRef, ringRef]);
};
